import { useState, useCallback, useEffect, useRef } from 'react';
import * as yup from 'yup';
import { useAppDispatch } from 'app/store';
import {
  federatedSignIn,
  signIn,
  signInWithPasskey,
  checkSSODomainThunk,
  resolveEmailToUsernameThunk,
  rememberUsername,
  forgetUsername,
} from 'app/store/authSlice';
import { isPasskeySupported } from 'app/auth/passkeyClient';
import { resolveSsoAttempt, startSsoAttempt } from 'app/auth/ssoAttemptLog';
import {
  notifySsoMode,
  readSsoSwitchMarker,
  relaunchSsoFromSwitchMarker,
  requestShellAccountSwitch,
  writeSsoSwitchMarker,
} from 'app/auth/ssoAccountVerification';
import { isMobileAppShell } from 'app/auth/nativePasskeyBridge';
import { captureException } from '@sentry/react';

type LoginStep = 'email' | 'password' | 'sso-redirect';

interface UseLoginFlowReturn {
  step: LoginStep;
  email: string;
  ssoProviderName: string | null;
  isCheckingSSO: boolean;
  isResolvingUsername: boolean;
  ssoError: string | null;
  handleEmailSubmit: (email: string, remember: boolean) => Promise<void>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handlePasswordSubmit: (
    password: string,
    remember: boolean
  ) => Promise<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error?: any;
    usedDeprecatedUsername?: boolean;
    /** The signed-in user's Cognito attributes on success (SEE-213: lets the
     * caller decide on the passkey-prompt redirect without waiting for the
     * store). */
    user?: { sub?: string; identities?: string };
  }>;
  resetToEmailStep: () => void;
  /** SEE-1350: from a rejection screen — sign the wrong account out and retry
   * the SSO attempt with the same typed email. `returnedIdentity` (the wrong
   * account) is remembered so a same-account return can escalate instead of
   * looping. */
  startAccountSwitch: (typedEmail: string, returnedIdentity?: string) => Promise<void>;
}

export function useLoginFlow(): UseLoginFlowReturn {
  const dispatch = useAppDispatch();
  // Start already covered when a switch is mid-flight: the login page is only
  // a waypoint between the error screen and Microsoft, so the email form must
  // never paint. relaunchSsoFromSwitchMarker (below) takes it from here.
  const [step, setStep] = useState<LoginStep>(() =>
    readSsoSwitchMarker()?.phase === 'switching' ? 'sso-redirect' : 'email'
  );
  const [email, setEmail] = useState('');
  const [ssoProviderName, setSsoProviderName] = useState<string | null>(null);
  const [isCheckingSSO, setIsCheckingSSO] = useState(false);
  const [isResolvingUsername, setIsResolvingUsername] = useState(false);
  const [ssoError, setSsoError] = useState<string | null>(null);
  const relaunchCheckedRef = useRef(false);

  // SEE-1350: second half of the account-switch flow. The mismatch screen
  // writes a sessionStorage marker and signs out; landing back on the login
  // page relaunches the SSO attempt with the originally typed email.
  useEffect(() => {
    if (relaunchCheckedRef.current) return;
    relaunchCheckedRef.current = true;
    const relaunched = relaunchSsoFromSwitchMarker((provider) => {
      notifySsoMode(false);
      dispatch(federatedSignIn({ federatedProvider: provider }));
    });
    if (relaunched) setStep('sso-redirect');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEmailSubmit = useCallback(
    async (inputEmail: string, remember: boolean) => {
      const trimmedEmail = inputEmail.trim().toLowerCase();
      setEmail(trimmedEmail);
      setSsoError(null);
      setSsoProviderName(null);
      setIsCheckingSSO(true);

      try {
        const action = await dispatch(checkSSODomainThunk({ email: trimmedEmail }));

        if (action.meta.requestStatus === 'fulfilled') {
          const result = action.payload as { isSSO: boolean; providerName: string | null };

          if (result.isSSO && result.providerName) {
            setSsoProviderName(result.providerName);
            setStep('sso-redirect');
            // Save or clear email for remember me before redirecting to Microsoft
            if (remember) {
              dispatch(rememberUsername({ username: trimmedEmail, persist: true }));
            } else {
              dispatch(forgetUsername());
            }
            // SEE-1200: every SSO attempt is logged start → success/failure.
            // The correlation id is persisted across the IdP redirect;
            // AuthProvider resolves success, the login screens resolve failure.
            startSsoAttempt({ provider: result.providerName, email: trimmedEmail });
            // SEE-1350: ask the shell for the SHARED browser jar (silent SSO).
            // Stated on every attempt so the shell's own private-jar default
            // stands whenever this code is absent — a web-only rollback then
            // restores SEE-1201's behavior with no app release.
            notifySsoMode(false);
            const ssoAction = await dispatch(federatedSignIn({ federatedProvider: result.providerName }));
            if (ssoAction.meta.requestStatus === 'rejected') {
              resolveSsoAttempt('failure', { failureCode: 'redirect_failed' });
              setSsoError('Failed to redirect to your company sign-in. Please try again.');
              setStep('email');
            }
          } else {
            // Non-SSO: try a passkey first (returning-user flow, Figma board
            // 4162). resolveEmailToUsername maps the email to the Cognito
            // username the passkey is keyed to; Auth.signIn(username) must
            // resolve to a real account before the CUSTOM_AUTH challenge can
            // start (speedchainr2CreateAuthChallenge treats an unresolved
            // username the same as "no passkey" and fails to the password
            // step), so a legacy-username account whose passkey login skipped
            // this resolve would never reach its passkey. On NO_PASSKEY /
            // cancel / failure we silently fall back to the password step.
            // isPasskeySupported covers the mobile shell too (SEE-217): true
            // only when the app has announced native-passkey support.
            let handled = false;
            if (isPasskeySupported()) {
              let username = trimmedEmail;
              try {
                const resolve = await dispatch(resolveEmailToUsernameThunk({ email: trimmedEmail }));
                if (resolve.meta.requestStatus === 'fulfilled') {
                  const r = resolve.payload as { statusCode: number; body: string };
                  if (r.statusCode === 200) username = r.body;
                }
              } catch (resolveError) {
                captureException(resolveError);
              }
              const pk = await dispatch(signInWithPasskey({ username, email: trimmedEmail, remember }));
              handled = pk.meta.requestStatus === 'fulfilled';
            }
            if (!handled) {
              setStep('password');
            }
          }
        } else {
          // If SSO check fails, fall back to password login
          setStep('password');
        }
      } catch (error) {
        captureException(error);
        setStep('password');
      } finally {
        setIsCheckingSSO(false);
      }
    },
    [dispatch]
  );

  const handlePasswordSubmit = useCallback(
    async (
      password: string,
      remember: boolean
    ): Promise<{
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      error?: any;
      usedDeprecatedUsername?: boolean;
      user?: { sub?: string; identities?: string };
    }> => {
      setIsResolvingUsername(true);

      try {
        let username = email;
        let resolvedFromEmail = false;

        try {
          const action = await dispatch(resolveEmailToUsernameThunk({ email }));

          if (action.meta.requestStatus === 'fulfilled') {
            const result = action.payload as { statusCode: number; body: string };

            if (result.statusCode === 200) {
              username = result.body;
              resolvedFromEmail = true;
            }
          }
          // If not found by email, use the raw input as username (backward compatible)
        } catch (resolveError) {
          // If the email lookup fails (network error, timeout), fall back to using
          // the raw input as the username. This ensures login isn't blocked by
          // infrastructure issues in the resolve endpoint.
          captureException(resolveError);
        }

        setIsResolvingUsername(false);

        const action = await dispatch(
          signIn({ username, password, remember, email: resolvedFromEmail ? email : undefined })
        );
        if (action.meta.requestStatus === 'rejected') {
          return { error: action.payload };
        }

        return {
          usedDeprecatedUsername: !yup.string().email().isValidSync(email),
          user: action.payload as { sub?: string; identities?: string },
        };
      } catch (error) {
        setIsResolvingUsername(false);
        captureException(error);
        return { error: { name: 'UnknownError', message: 'An error occurred. Please try again.' } };
      }
    },
    [dispatch, email]
  );

  const resetToEmailStep = useCallback(() => {
    setStep('email');
    setSsoProviderName(null);
    setSsoError(null);
  }, []);

  // SEE-1350: rejection-screen "Use a different Microsoft account". The user
  // is NOT authenticated here (the gates rejected the wrong account), so no
  // sign-out is needed — clear the cached sessions (shell sheet chain) and
  // relaunch the attempt with the same typed email.
  const startAccountSwitch = useCallback(
    async (typedEmail: string, returnedIdentity?: string) => {
      const trimmedEmail = typedEmail.trim().toLowerCase();
      // Cover the screen BEFORE the provider lookup: that await is a network
      // round trip, and leaving the login form visible through it flashes the
      // app/form between the error screen and Microsoft.
      setStep('sso-redirect');
      let provider = ssoProviderName;
      if (!provider) {
        const action = await dispatch(checkSSODomainThunk({ email: trimmedEmail }));
        const result = action.payload as { isSSO?: boolean; providerName?: string | null } | undefined;
        provider = result?.providerName ?? null;
      }
      if (!provider) {
        // Network blip mid-switch, or the domain no longer resolves to an IdP.
        // Without a message this silently drops the user on the email form and
        // the button looks broken, so say something (same copy path as the
        // initial SSO redirect failure).
        resolveSsoAttempt('failure', { failureCode: 'redirect_failed' });
        setSsoError('Failed to redirect to your company sign-in. Please try again.');
        setEmail(trimmedEmail);
        setStep('email');
        return;
      }
      writeSsoSwitchMarker({
        typedEmail: trimmedEmail,
        provider,
        expectIdentity: returnedIdentity?.toLowerCase(),
        phase: 'retried',
        startedAt: Date.now(),
      });
      if (isMobileAppShell()) {
        await requestShellAccountSwitch();
      }
      startSsoAttempt({ provider, email: trimmedEmail });
      notifySsoMode(false);
      const ssoAction = await dispatch(federatedSignIn({ federatedProvider: provider }));
      if (ssoAction.meta.requestStatus === 'rejected') {
        resolveSsoAttempt('failure', { failureCode: 'redirect_failed' });
        setStep('email');
      }
    },
    [dispatch, ssoProviderName]
  );

  return {
    step,
    email,
    ssoProviderName,
    isCheckingSSO,
    isResolvingUsername,
    ssoError,
    handleEmailSubmit,
    handlePasswordSubmit,
    resetToEmailStep,
    startAccountSwitch,
  };
}
